import { PUBLIC_AUCTION_ADDRESS, PUBLIC_CHAIN_ID } from '$env/static/public';

import { writable, type Writable } from 'svelte/store';

import { useReadContract, useWaitTransaction, useWriteContract } from '@do/svelte-web3-manager';

import type { HashString } from '@shared/types';
import { hashToKeccak } from '@shared/utils/blockchain';

import { SaleState, defaultData, handleApproveSuccess } from '../configs';
import auctionAbi from '../configs/AuctionABI.json';
import erc20ABIweb from '../configs/erc20ABI.json';
import type { AuctionContract, SaleModel } from '../types';

const createSaleModel = (modelData: SaleModel) => {
  const { set, subscribe }: Writable<SaleModel> = writable(modelData);
  let writeContract: ReturnType<typeof useWriteContract>;
  let readContract: ReturnType<typeof useReadContract>;
  let waitForTransaction: ReturnType<typeof useWaitTransaction>;

  const checkVariables = () => {
    // first of all, check if the public token address and chain ID
    if (!PUBLIC_AUCTION_ADDRESS) {
      console.error('Public token address not found');
      return;
    }
    if (!PUBLIC_CHAIN_ID) {
      console.error('Chain ID not found');
      return;
    }
  };

  return {
    subscribe,
    set,
    init() {
      checkVariables();
      writeContract = useWriteContract();
      readContract = useReadContract();
      waitForTransaction = useWaitTransaction();
    },
    async bidInitial(auctionId: string, price: bigint) {
      const convertedId = hashToKeccak(auctionId);

      const hash = await writeContract({
        address: PUBLIC_AUCTION_ADDRESS as HashString,
        abi: auctionAbi,
        functionName: 'raiseInitial',
        args: [convertedId, price],
      });

      const response = await waitForTransaction({ hash });

      if (response.status !== 'success') throw { shortMessage: 'An error occurred' };
    },
    async bid(auctionId: string, price: bigint) {
      const convertedId = hashToKeccak(auctionId);

      const hash = await writeContract({
        address: PUBLIC_AUCTION_ADDRESS as HashString,
        abi: auctionAbi,
        functionName: 'raise',
        args: [convertedId, price],
      });

      const response = await waitForTransaction({ hash });

      if (response.status !== 'success') throw { shortMessage: 'An error occurred' };
    },
    async approveERC20Transfer(contractAddress: HashString, amount: bigint, walletAddress: HashString) {
      const balance = (await readContract({
        address: contractAddress,
        abi: erc20ABIweb,
        functionName: 'balanceOf',
        args: [walletAddress],
      })) as bigint;

      if (balance < amount)
        throw { shortMessage: 'Insufficient funds to complete the transaction. Please top up your USDC balance.' };

      const allowance = (await readContract({
        address: contractAddress,
        abi: erc20ABIweb,
        functionName: 'allowance',
        args: [walletAddress, PUBLIC_AUCTION_ADDRESS],
      })) as bigint;

      if (allowance >= amount) {
        set({ state: SaleState.Pending });
        return;
      }

      const hashApprove = await writeContract({
        address: contractAddress,
        abi: erc20ABIweb,
        functionName: 'approve',
        args: [PUBLIC_AUCTION_ADDRESS, amount],
      });

      const resApprove = await waitForTransaction({ hash: hashApprove });

      if (resApprove.status !== 'success') throw { shortMessage: 'Error during transaction approve' };

      set({ state: SaleState.Pending });
      handleApproveSuccess();
    },
    async getAuction(auctionId: string): Promise<AuctionContract> {
      const convertedId = hashToKeccak(auctionId);

      const auction = (await readContract({
        address: PUBLIC_AUCTION_ADDRESS as HashString,
        abi: auctionAbi,
        functionName: 'auction',
        args: [convertedId],
      })) as AuctionContract;

      return auction;
    },
  };
};

export const saleModel = createSaleModel(defaultData);
